import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { installAnalytics } from "@/lib/analytics";

// Key used to persist the user's cookie choice. Change the version suffix
// if the categories ever change so we re-prompt users for consent.
const STORAGE_KEY = "nl_cookie_consent";
const VERSION = 1;

/**
 * GDPR / ePrivacy cookie-consent banner.
 *
 * Categories:
 *   - Strictly necessary  (always allowed, no consent required)
 *   - Analytics           (Google Analytics 4 — opt-in only)
 *
 * If the user has never chosen, the banner is shown. When "Accept analytics"
 * is clicked, GA4 boots via installAnalytics(). "Necessary only" persists a
 * negative decision so we never fire GA on subsequent visits.
 */
export default function CookieConsent() {
  const [decision, setDecision] = useState(() => {
    if (typeof window === "undefined") return "loaded-accepted";
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.version !== VERSION) return null;
      return parsed.choice;
    } catch {
      return null;
    }
  });

  // On mount: if consent was previously granted, boot analytics silently.
  useEffect(() => {
    if (decision === "accepted") installAnalytics();
  }, [decision]);

  if (decision === "accepted" || decision === "rejected") return null;

  const persist = (choice) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: VERSION, choice, at: new Date().toISOString() }),
      );
    } catch {
      /* localStorage disabled — banner will reappear next visit, which is acceptable */
    }
    setDecision(choice);
    if (choice === "accepted") installAnalytics();
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 pointer-events-none"
    >
      <div className="mx-auto max-w-4xl pointer-events-auto bg-[var(--hh-surface)] border border-[var(--hh-border)] shadow-2xl p-5 sm:p-7">
        <span className="label-eyebrow text-[var(--hh-accent)]">Cookies</span>
        <h2 className="font-display text-2xl mt-2 text-[var(--hh-cream)]">Your cookie choices</h2>
        <p className="mt-3 text-sm text-[var(--hh-sage)] leading-relaxed">
          We use strictly-necessary cookies to keep you signed in. With your consent we also load{" "}
          <strong className="text-[var(--hh-cream)]">Google Analytics 4</strong> to understand which pages
          are most useful. No advertising cookies, no data sold to third parties. Details on the{" "}
          <Link to="/cookies" className="text-[var(--hh-accent)] underline underline-offset-2">
            Cookie Notice
          </Link>
          .
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => persist("accepted")}
            className="btn-primary text-sm"
            data-testid="cookie-consent-accept-btn"
          >
            Accept all cookies
          </button>
          <button
            type="button"
            onClick={() => persist("rejected")}
            className="btn-ghost text-sm"
            data-testid="cookie-consent-reject-btn"
          >
            Accept only necessary
          </button>
        </div>
      </div>
    </div>
  );
}
